import { toolRegistry } from "@/services/toolRegistry";
import type { ToolDefinition, ToolInvokeResult } from "@/types/toolRegistry";

export interface PluginToolOperation {
  name: string;
  description?: string;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  sideEffect?: boolean;
  requiresApproval?: boolean;
}

export interface PluginTool {
  name: string;
  label?: string;
  description?: string;
  operations: PluginToolOperation[];
}

export interface PluginToolResult {
  success: boolean;
  output?: unknown;
  error?: string;
  needsPermission?: boolean;
  artifact?: Record<string, unknown>;
}

export type PluginToolExecutor = (
  operation: string,
  input: Record<string, unknown>
) => Promise<PluginToolResult>;

const PLUGIN_TOOL_NAME_REGEX = /^[a-z0-9][a-z0-9_-]{0,40}$/;
const pluginToolIds = new Map<string, Set<string>>();

export function pluginToolId(pluginId: string, name: string): string {
  return `plugin:${pluginId}:${name}`;
}

function trackTool(pluginId: string, toolId: string): void {
  let ids = pluginToolIds.get(pluginId);
  if (!ids) {
    ids = new Set();
    pluginToolIds.set(pluginId, ids);
  }
  ids.add(toolId);
}

export function registerPluginTool(
  pluginId: string,
  pluginName: string,
  tool: PluginTool,
  executor: PluginToolExecutor
): string {
  if (!PLUGIN_TOOL_NAME_REGEX.test(tool.name)) {
    throw new Error(`Invalid plugin tool name "${tool.name}"`);
  }

  const toolId = pluginToolId(pluginId, tool.name);
  const executorKey = toolId;

  const definition: ToolDefinition = {
    id: toolId,
    name: tool.label ?? `${pluginName}: ${tool.name}`,
    description: tool.description,
    provider: pluginId,
    permissions: [],
    executorKey,
    isBuiltin: false,
    operations: tool.operations.map((op) => ({
      name: op.name,
      description: op.description,
      inputSchema: op.inputSchema ?? { type: "object" },
      outputSchema: op.outputSchema ?? { type: "object" },
      sideEffect: op.sideEffect ?? false,
      requiresApproval: op.requiresApproval,
    })),
  };

  toolRegistry.register(definition, async (operation, input) => {
    const result = await executor(operation, input);
    return {
      success: result.success,
      output: result.output,
      error: result.error,
      needsPermission: result.needsPermission,
      artifact: result.artifact as ToolInvokeResult["artifact"],
    };
  });

  trackTool(pluginId, toolId);
  return toolId;
}

export function unregisterPluginTool(pluginId: string, toolId: string): void {
  const expectedPrefix = `plugin:${pluginId}:`;
  if (!toolId.startsWith(expectedPrefix)) {
    throw new Error(`Cannot unregister foreign tool "${toolId}" from plugin "${pluginId}"`);
  }
  toolRegistry.unregister(toolId);
  pluginToolIds.get(pluginId)?.delete(toolId);
}

export function unregisterAllPluginTools(pluginId: string): void {
  const ids = pluginToolIds.get(pluginId);
  if (!ids) return;
  for (const toolId of Array.from(ids)) {
    toolRegistry.unregister(toolId);
  }
  pluginToolIds.delete(pluginId);
}
